import React, { useState } from "react"

import { useContext, useEffect, useRef } from "react"
import { useCookies } from "react-cookie"
import IOrder from "../../../interfaces/IOrder"
import { AdminContext } from "~/contexts/AdminContext"
import { useDebouncedCallback } from "use-debounce"
import api from "../../../api2"
import ILineItem from "../../../interfaces/ILineItem"
import { mapLineItemToParams } from "~/api2/orders"
import * as Sentry from "@sentry/react"
import PaymentTypeSelector from "../../Reservations/PaymentModal/PaymentTypeSelector"
import ePaymentType from "../../../enums/ePaymentType"
import serviceRateModeForPaymentType from "../../../utils/serviceRateModeForPaymentType"
import DiscountProps from "../../../interfaces/DiscountProps"
import IAddOn from "../../../interfaces/IAddOn"
import AdminAddOns from "../../Reservations/PaymentModal/AdminAddOns"
import { Switch, Transition } from "@headlessui/react"
import Spinner from "../../ui/Spinner"
import classNames from "../../../utils/classNames"
import { CubeIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { formatMoneyFromCents } from "~/utils/formatMoney"
import OrderSummary from "../Orders/OrderSummary"
import Button from "../../ui/Button"
import StripeCardForm from "../../Reservations/PaymentModal/StripeCardForm"
import StoreSearchBar from "./StoreSearchBar"
import CamperSearchBar from "./CamperSearchBar"
import ICamper from "../../../interfaces/ICamper"
import CamperCard from "../../ui/CamperCard"
import PaymentLinkForm from "../../Reservations/PaymentModal/PaymentLinkForm"
import ProductModalContent from "../Settings/Product/ProductModalContent"
import Modal from "../../ui/Modal"
import { CheckCircleIcon, TrashIcon, ChevronDownIcon } from "@heroicons/react/24/solid"
import { useReactToPrint } from "react-to-print"
import { useStoreContent } from "./useStoreContent"
import useSetTaxFees from "../Invoices/hooks/useSetTaxFees"
import StoreTabControl from "~/components/store/StoreTabControl"
import StoreHistory from "~/components/store/StoreHistory"
import OrderDetailPanel from "~/components/store/OrderDetailPanel"
import SelectInput from "../../ui/SelectInput"
import usePagination from "~/hooks/usePagination"
import { useModal } from "~/contexts/ModalProvider"
import { useLocation } from "react-router-dom"
import { PaymentMethodType } from "~/interfaces/IPaymentMethods"
import PaymentAnimation from "~/components/ui/PaymentAnimation"
import ConfirmationModal from "~/components/Reservations/ConfirmationModal"

// Define a constant for the cookie name
const STORE_SORT_OPTION_COOKIE = "store_sort_option"

const StoreContent: React.FC<StoreContentProps> = () => {
  const { page, setPage, perPage } = usePagination()

  const [selectedOrder, setSelectedOrder] = useState(null)
  const {
    lineItems,
    setLineItems,
    handleRemoveItem,
    camp,
    campingStyles,
    openModal,
    closeModal,
    showNotification,
    activeTab,
    changeTab,
  } = useStoreContent()

  const [isLoading, setIsLoading] = useState(false)
  const [products, setProducts] = useState<IAddOn[]>([])
  const [order, setOrder] = useState<IOrder>({} as IOrder)
  const [orderWithFees, setOrderWithFees] = useState<IOrder>({} as IOrder)
  const [paymentMethod, setPaymentMethod] = useState<ePaymentType>(ePaymentType.CARD_INPUT)
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<PaymentMethodType>()
  const [isEnabledServiceFee, setIsEnabledServiceFee] = useState(
    [ePaymentType.CARD_INPUT, ePaymentType.LINK, ePaymentType.CARD_READER].includes(paymentMethod),
  )
  const [filter, setFilter] = useState("")
  const [camperFilter, setCamperFilter] = useState()
  const [selectedCamper, setSelectedCamper] = useState<ICamper>()
  const [paymentStatus, setPaymentStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState("")
  const [intervalId, setIntervalId] = useState<number>()
  const [showCloseButton, setShowCloseButton] = useState(false)
  const campingStyleId = campingStyles?.find((cs) => cs.credit_card_rate)?.id || campingStyles?.[0]?.id
  const [isSuccess, setIsSuccess] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isPrintLoading, setIsPrintLoading] = useState(false)
  const [cookies, setCookie] = useCookies([STORE_SORT_OPTION_COOKIE])
  const [sortOption, setSortOption] = useState<string>(cookies[STORE_SORT_OPTION_COOKIE] || "alphabetical-asc")
  const {
    taxFeeEnabled: isEnabledTaxFee,
    creditCardFeeEnabled: isEnabledCreditCardFee,
    setCreditCardFeeEnabled: setIsEnabledCreditCardFee,
    TaxToggle,
  } = useSetTaxFees({
    order: order,
    campingStyle: campingStyles?.find((cs) => cs.id === campingStyleId),
    isStore: true,
    defaultTaxFeeEnabled: Number(camp?.tax_rate || camp?.store_tax_rate) > 0,
    defaultCreditCardFeeEnabled: Number(campingStyles?.find((cs) => cs.credit_card_rate)?.credit_card_rate) > 0,
    paymentMethod,
  })

  const receiptPrintRef = useRef(null)

  const hasSortValues = products?.some((addon) => addon.sort !== undefined && addon.sort !== null)

  const sortOptions = [
    { label: "Alphabetical (A-Z)", value: "alphabetical-asc" },
    { label: "Alphabetical (Z-A)", value: "alphabetical-desc" },
    { label: "Price (Low-High)", value: "price-asc" },
    { label: "Price (High-Low)", value: "price-desc" },
  ]

  const sortProducts = (addons: IAddOn[]) => {
    if (!addons) return []

    switch (sortOption) {
      case "alphabetical-asc":
        return [...addons].sort((a, b) => a.label.localeCompare(b.label))
      case "alphabetical-desc":
        return [...addons].sort((a, b) => b.label.localeCompare(a.label))
      case "price-asc":
        return [...addons].sort((a, b) => a.amount_in_cents - b.amount_in_cents)
      case "price-desc":
        return [...addons].sort((a, b) => b.amount_in_cents - a.amount_in_cents)
      default:
        return [...addons].sort((a, b) => a.label.localeCompare(b.label))
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [camp])

  useEffect(() => {
    if (open) buildOrder()
  }, [lineItems, isEnabledTaxFee, isEnabledCreditCardFee, paymentMethod, selectedCamper])

  useEffect(() => {
    if (activeTab === "pos") {
      setSelectedOrder(null)
    }
  }, [activeTab])

  const fetchProducts = async (): Promise<void> => {
    api.Products.forCamp(camp.id)
      .then(({ products: p }) => {
        setProducts(p)
      })
      .catch((err) => {
        console.error(err)
      })
  }

  const buildOrder = useDebouncedCallback(() => {
    if (!camp) return

    fetchPreview()
      .then(({ order: { preview } }) => {
        setOrder(preview)
        setIsLoading(false)
      })
      .catch((err) => {
        console.error(err)
        Sentry.captureException(err)
        setIsLoading(false)
      })

    if (!isEnabledTaxFee) {
      fetchPreview({ forceFeesEnabled: true })
        .then(({ order: { preview } }) => {
          setOrderWithFees(preview)
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }, 100)

  const fetchPreview = async ({ forceFeesEnabled = false } = {}): Promise<any> => {
    return api.Orders.createPreview({
      order_id: order.id,
      camp_id: camp.id,
      camping_style_id: campingStyleId,
      camper_id: selectedCamper?.id,
      include_payment_methods: true,
      is_credit_card_fee_enabled: forceFeesEnabled || isEnabledCreditCardFee,
      is_service_fee_enabled: forceFeesEnabled || isEnabledServiceFee,
      service_rate_mode: serviceRateModeForPaymentType(paymentMethod),
      is_store: true,
      discount_slug: discountFromLineItems(lineItems)?.slug,
      line_items_attributes: lineItems.map((li) => mapLineItemToParams(li, forceFeesEnabled || isEnabledTaxFee)),
    })
  }

  const discountFromLineItems = (lineItems: Partial<ILineItem>[]): DiscountProps => {
    const discountIds = lineItems?.map((li) => li.discount_id) || []
    return camp.discounts.find((discount) => discountIds.includes(discount.id))
  }

  const handleChangePaymentType = (v: ePaymentType) => {
    setPaymentMethod(v)
    if (v === ePaymentType.CASH_PAID || v === ePaymentType.CASH_UNPAID) {
      setIsEnabledCreditCardFee(order.credit_card_rate > 0)
      setIsEnabledServiceFee(false)
    } else {
      setIsEnabledCreditCardFee(order.credit_card_rate > 0)
      setIsEnabledServiceFee(true)
    }
  }

  const onAutofill = (camper: ICamper) => {
    setSelectedCamper(camper)
  }

  const handleClickTakeCashPayment = () => {
    setIsProcessing(true)
    api.Orders.createPayment({
      order_id: order.id,
      camp_id: camp.id,
      camper_id: selectedCamper?.id,
      camping_style_id: campingStyleId,
      is_credit_card_fee_enabled: isEnabledCreditCardFee,
      discount_slug: discountFromLineItems(lineItems)?.slug,
      is_store: true,
      line_items_attributes: lineItems.map((li) => mapLineItemToParams(li, isEnabledTaxFee)),
    })
      .then(({ payment }) => {
        setIsSuccess(true)
        setIsProcessing(false)
      })
      .catch((err) => {
        console.error(err)
        showNotification({ type: "error", title: err.message })
        setIsProcessing(false)
      })
  }

  const handleProductClick = (selectedProduct?: IAddOn) => {
    openModal("generic", {
      children: <ProductModalContent fetchProducts={fetchProducts} selectedProduct={selectedProduct} />,
      onClose: () => {
        closeModal()
      },
    })
  }

  const shownProducts = sortProducts(
    products?.filter(
      (addon) =>
        !["always-applied", "hidden"].includes(addon.kind) &&
        addon.addable_camping_style_id === null &&
        (addon.label.toLowerCase().includes(filter.toLowerCase()) ||
          (addon.sku && addon.sku.toLowerCase().includes(filter.toLowerCase())) ||
          (addon.description && addon.description.toLowerCase().includes(filter.toLowerCase()))),
    ),
  )

  const clearStore = (): boolean => {
    setOrder({} as IOrder)
    setSelectedCamper(null)
    setCamperFilter(null)
    setFilter("")
    setLineItems([])
    setPaymentMethod(ePaymentType.CARD_INPUT)
    setPaymentStatus(null)
    setErrorMessage("")

    return true
  }

  const clearCamper = () => {
    setSelectedCamper(null)
    setCamperFilter(null)
  }

  const usePrint = useReactToPrint({
    content: () => receiptPrintRef.current,
    onAfterPrint: () => setIsPrintLoading(false),
    onBeforePrint: () => setIsPrintLoading(true),
  })

  if (!camp) return

  return (
    <div className="w-full mt-20 px-1">
      <div className="grid grid-cols-1 md:grid-cols-3 md:gap-8 h-[calc(100svh-7rem)]">
        <div className={classNames("col-span-2 md:border-r md:border-gray-200 md:overflow-hidden", activeTab === "pos" && "flex flex-col")}>
          <div className="mb-4  md:pr-8">
            <StoreTabControl activeTab={activeTab} setActiveTab={changeTab} />
          </div>

          {activeTab === "pos" ? (
            <>
              <div className="flex-1 md:pr-8 pt-4 border-2 md:border-none border-white md:pt-0 flex-wrap flex items-center pb-4 justify-between w-full bg-white border-b z-10 sticky -top-[20px] gap-2 ">
                <div className="flex gap-2 items-center flex-grow">
                  <StoreSearchBar setFilter={setFilter} />
                  <SelectInput
                    className="w-48"
                    options={sortOptions}
                    selected={sortOptions.find((option) => option.value === sortOption) || sortOptions[0]}
                    setSelected={(option) => {
                      setSortOption(option.value)
                      setCookie(STORE_SORT_OPTION_COOKIE, option.value, { path: "/" })
                    }}
                    noIcon={true}
                  />
                </div>
                <div>
                  <Button
                    variant={isEditing ? "blue" : "white"}
                    onClick={() => {
                      if (isEditing) {
                        setIsEditing(false)
                      } else {
                        setIsEditing(true)
                      }
                    }}
                    className={classNames("flex-shrink-0", isEditing && "animate-pulse")}
                  >
                    {isEditing ? "Stop editing" : "Edit products"}
                  </Button>
                  <Button variant="blue" onClick={() => handleProductClick()} className="flex-shrink-0 ml-2">
                    Add product
                  </Button>
                </div>
              </div>
              <div className="overflow-y-auto overflow-x-hidden h-full md:pr-8">
                <AdminAddOns
                  addOns={sortProducts(shownProducts)}
                  primaryColor={camp?.primary_color}
                  lineItems={lineItems}
                  setLineItems={setLineItems as (v: Array<Partial<ILineItem>>) => void}
                  handleProductClick={handleProductClick}
                  isEditing={isEditing}
                />
              </div>
            </>
          ) : (
            <div className="h-full overflow-y-auto md:pr-8">
              <StoreHistory
                setSelectedOrder={setSelectedOrder}
                selectedOrderId={selectedOrder?.id}
                page={page}
                setPage={setPage}
                perPage={perPage}
              />
            </div>
          )}
        </div>

        <div className="md:max-w-lg w-full md:overflow-hidden ml-auto">
          {isLoading ? (
            <Transition
              show={isLoading}
              enter="transition-opacity ease-out duration-75"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-in duration-75"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="z-10 absolute w-full h-full bg-white opacity-50 flex items-center justify-center">
                <Spinner size={6} />
              </div>
            </Transition>
          ) : (
            <div className="flex flex-col justify-between w-full h-full overflow-y-auto overflow-x-hidden scrollbar-gutter-stable pr-3 space-y-2">
              {activeTab === "history" ? (
                <OrderDetailPanel
                  order={selectedOrder}
                  setOrder={setSelectedOrder}
                  camp={camp}
                  page={page}
                  perPage={perPage}
                />
              ) : (
                <>
                  <div className="md:row-start-1 md:row-end-7 flex flex-col overflow-hidden">
                    {selectedCamper ? (
                      <CamperCard camper={selectedCamper} onClose={() => clearCamper()} />
                    ) : (
                      <CamperSearchBar
                        value={camperFilter}
                        onChange={(e) => setCamperFilter(e.target.value)}
                        onAutofill={onAutofill}
                      />
                    )}
                    <div className="overflow-y-auto mt-3 space-y-2">
                      {lineItems.map((line_item) => (
                        <div className="flex items-center space-x-2">
                          <div className="w-full rounded-md border border-gray-300 px-4 py-2">
                            <div className="flex justify-between items-center">
                              <div className="flex flex-col">
                                <label htmlFor="name" className="flex items-center text-base text-gray-900">
                                  <span>{line_item.label}</span>
                                  <XMarkIcon className="w-4 h-4 inline mx-1 mt-0.5" />
                                  <span>{line_item.quantity}</span>
                                </label>
                              </div>
                              <div className="flex space-x-3 items-center -mr-1">
                                <span className="text-gray-900">
                                  <span>
                                    {formatMoneyFromCents(
                                      line_item.unit_amount_in_cents * line_item.quantity,
                                      camp?.currency,
                                    )}
                                  </span>
                                </span>
                                <div
                                  onClick={() => handleRemoveItem(line_item.add_on_id)}
                                  className="flex items-center justify-center w-8 h-full p-[4px] rounded-md cursor-pointer hover:bg-gray-100"
                                >
                                  <TrashIcon className="w-5 h-5 inline hover:text-gray-700 text-gray-600" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="md:row-start-7 md:row-end-13 flex bg-white flex-col-reverse">
                    <div className="pb-4">
                      <div className="mb-2 flex justify-between items-center">
                        <label htmlFor="name" className="inline font-bold text-md text-gray-900">
                          Payment method
                        </label>
                      </div>
                      <PaymentTypeSelector
                        options={[ePaymentType.CARD_INPUT, ePaymentType.LINK, ePaymentType.CASH_PAID]}
                        selectedOption={paymentMethod}
                        onChange={handleChangePaymentType}
                      />
                      {paymentMethod === ePaymentType.CASH_PAID ? (
                        <div className="flex justify-end items-center mt-6">
                          <Button
                            variant="blue"
                            onClick={handleClickTakeCashPayment}
                            disabled={!lineItems?.length || Number(order?.total_in_cents) <= 0 || isProcessing}
                          >
                            Take offline payment
                          </Button>
                        </div>
                      ) : paymentMethod === ePaymentType.LINK ? (
                        <PaymentLinkForm
                          order={order}
                          email={selectedCamper?.email}
                          campingStyleId={campingStyleId}
                          onSuccess={() => setIsSuccess(true)}
                          isTaxFeeEnabled={isEnabledTaxFee}
                          isCreditCardFeeEnabled={isEnabledCreditCardFee}
                          discountSlug={discountFromLineItems(lineItems)?.slug}
                          lineItems={lineItems}
                          depositLineItems={[]}
                          isStore={true}
                        />
                      ) : (
                        <StripeCardForm
                          ctaText={`Charge ${formatMoneyFromCents(order?.unpaid_total_in_cents, camp?.currency)}`}
                          setPaymentIntentId={() => {}}
                          paymentIntentId={null}
                          onSuccess={() => setIsSuccess(true)}
                          order={order}
                          camperId={selectedCamper?.id}
                          isCamperPresent={!!selectedCamper}
                          camper={selectedCamper}
                          campingStyleId={campingStyleId}
                          isTaxFeeEnabled={isEnabledTaxFee}
                          isCreditCardFeeEnabled={isEnabledCreditCardFee}
                          discountSlug={discountFromLineItems(lineItems)?.slug}
                          lineItems={lineItems}
                          depositLineItems={[]}
                          paymentMethod={paymentMethod}
                          setPaymentMethod={setPaymentMethod}
                          errorMessage={errorMessage}
                          setErrorMessage={setErrorMessage}
                          setPaymentStatus={setPaymentStatus}
                          setIntervalId={setIntervalId}
                          setShowCloseButton={setShowCloseButton}
                          isStore={true}
                          selectedPaymentMethodType={selectedPaymentMethodType}
                          setSelectedPaymentMethodType={setSelectedPaymentMethodType}
                        />
                      )}
                    </div>
                    <div className="w-full flex flex-col mt-3 border-t border-gray-200 pt-3 mb-3">
                      {TaxToggle}
                      <OrderSummary order={order} camp={camp} isPreview />
                    </div>
                    <div className="w-full mt-3 border-t border-gray-200 hidden">
                      <div className="m-8" ref={receiptPrintRef}>
                        <OrderSummary order={order} camp={camp} isPreview isIncludingLineItems={true} />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <Modal
        open={isSuccess}
        onClose={() => {
          clearStore()
          setIsSuccess(false)
        }}
        maxWidth="max-w-xl"
      >
        <div className="">
          <div className="text-center flex flex-col items-center">
            <PaymentAnimation type="success" />
             <h3 className="mb-12 text-xl font-bold text-green-500">
              {paymentMethod === ePaymentType.LINK ? "Payment link sent" : "Payment successful"}
            </h3>
          </div>
          <div className="flex justify-center mt-6 gap-3">
            <Button variant="gray" onClick={() => usePrint()} className="flex items-center" disabled={isPrintLoading}>
              {isPrintLoading ? (
                <>
                  <Spinner className="mr-2" size={4} />
                  Printing...
                </>
              ) : (
                "Print receipt"
              )}
            </Button>
            <Button
              variant="blue"
              onClick={() => {
                clearStore()
                setIsSuccess(false)
              }}
            >
              Close and clear order
            </Button>
          </div>
        </div>
      </Modal>
      {(errorMessage?.length || paymentStatus) && (
        <ConfirmationModal
          paymentStatus={errorMessage?.length ? "failed" : paymentStatus}
          setPaymentStatus={setPaymentStatus}
          onCtaClick={undefined}
          paymentMethod={paymentMethod}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          showCloseButton={false}
          setShowCloseButton={() => {}}
          camp={camp}
          paymentIntentId={undefined}
          intervalId={undefined}
        />
      )}
    </div>
  )
}

export default StoreContent
